// Липкие флаги гейтов верификации жестов и модерации фото (1в1 с приложением,
// там AsyncStorage). Переживают перезагрузку: по ним гейт срабатывает сразу на
// старте, ещё до ответа профиля, а потом сверяется с бэком.

export const FLAG = {
  verificationRejected: "verificationRejected",
  verificationPending: "verificationPending",
  verificationPhotoUri: "verificationPhotoUri",
  verificationGestureType: "verificationGestureType",
  verificationSubmittedAt: "verificationSubmittedAt",
  photoRejected: "photoRejected",
  lastMainPhotoLocalUri: "lastMainPhotoLocalUri",
} as const;

// Старый ключ селфи (до переноса спеки) — читаем как фолбэк.
const LEGACY_PHOTO_URI = "verifyPhotoUri";

type FlagKey = typeof FLAG[keyof typeof FLAG];

export function getFlag(key: FlagKey): string | null {
  try {
    const v = window.localStorage.getItem(key);
    if (v === null && key === FLAG.verificationPhotoUri) {
      return window.localStorage.getItem(LEGACY_PHOTO_URI);
    }
    return v;
  } catch (e) {
    return null;
  }
}

export function setFlag(key: FlagKey, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    /* квота/приватный режим — не критично */
  }
}

export function removeFlag(key: FlagKey): void {
  try {
    window.localStorage.removeItem(key);
    if (key === FLAG.verificationPhotoUri) {
      window.localStorage.removeItem(LEGACY_PHOTO_URI);
    }
  } catch (e) {
    /* noop */
  }
}

export const isFlagOn = (key: FlagKey) => getFlag(key) === "1";

export function getStoredGestureType(): number | null {
  const raw = getFlag(FLAG.verificationGestureType);
  if (raw === null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

// Картинки примеров жестов с бэка, как в приле: verif-{n}-female.png,
// индексируются gestureType в порядке [2,3,1,4,5].
const VERIF_FEMALE_ORDER = [2, 3, 1, 4, 5];
const API = process.env.REACT_APP_BACKEND_URL || "";
const HOST = API.replace(/\/api\/?$/, "");
export const GESTURE_STEPS = VERIF_FEMALE_ORDER.map((n) => `${HOST}/mobile/verif-${n}-female.png`);

export function gestureExampleUri(gesture?: number | null): string {
  const g = typeof gesture === "number" ? gesture : getStoredGestureType();
  return (typeof g === "number" && GESTURE_STEPS[g]) || GESTURE_STEPS[0];
}

// Файл → ужатый data-URL. В отличие от blob: (URL.createObjectURL) переживает
// переход и перезагрузку, поэтому годится для localStorage (селфи на экранах
// review/error, превью отклонённого фото).
export const fileToPersistentUri = (file: File, maxSide = 1080, quality = 0.85): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const img = new Image();
      img.onerror = () => reject(new Error("image decode failed"));
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { resolve(dataUrl); return; }
        ctx.drawImage(img, 0, 0, w, h);
        try {
          resolve(canvas.toDataURL("image/jpeg", quality));
        } catch (err) {
          resolve(dataUrl);
        }
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
